import { Fragment } from 'react';
import { Grid, GridChild, InlineNotification, NotificationType } from '@shipengine/giger';
import { Currency } from '@packlink/packlink-sdk';
import { useTranslation, Trans } from '@packlink/translation-provider';
import { InsuranceInformation } from './InsuranceInformation.js';
import { InsuranceCardInfo } from './InsuranceCard/InsuranceCardInfo.js';
import { getInsuranceMaxCoverageRow, getInsuranceMaxCoverage } from './InsuranceSelectorStyles.js';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';
import { InsuranceCard } from './InsuranceCard/InsuranceCard.js';
import { UpsellInsuranceType, InsuranceTitle, InsuranceBenefit } from '../types/upsell.js';

const InsuranceSelector = props => {
  const {
    name,
    imageUrl,
    insuranceTypeSelected,
    insurancesToDisplay,
    onChange,
    currency,
    contentValue,
    isSecondHand = false,
    hasVerticalLayout = false,
    numberShipmentsInBulk,
    showInsuranceInformation = true,
    useHalfWidth = false,
    hasProofOfDelivery = false
  } = props;
  const {
    t
  } = useTranslation();
  const showProofOfDelivery = !!insurancesToDisplay[UpsellInsuranceType.PROOF_OF_DELIVERY] || hasProofOfDelivery;
  const infoPosition = !hasVerticalLayout || useHalfWidth ? {
    rowStart: 1,
    colStart: 7
  } : undefined;
  const displayMaxCoverageInNewRow = !hasVerticalLayout && !showInsuranceInformation && useHalfWidth;
  const getFormattedAmount = (type, amount) => {
    if (typeof amount === 'number' && type !== UpsellInsuranceType.PACKLINK_INSURANCE && type !== UpsellInsuranceType.NO_INSURANCE) {
      return Currency.format(amount, currency);
    }
    return '';
  };
  return jsxs(Grid, {
    noPadding: true,
    children: [Object.keys(insurancesToDisplay).map(insuranceType => {
      var _insuranceToDisplay$m;
      const insuranceToDisplay = insurancesToDisplay[insuranceType];
      const isChecked = insuranceTypeSelected === insuranceType;
      const maxCoverage = (_insuranceToDisplay$m = insuranceToDisplay === null || insuranceToDisplay === void 0 ? void 0 : insuranceToDisplay.maxCoverage) !== null && _insuranceToDisplay$m !== void 0 ? _insuranceToDisplay$m : 0;
      const formattedMaxCoverage = Currency.format(maxCoverage, currency);
      const formattedAmount = getFormattedAmount(insuranceType, insuranceToDisplay === null || insuranceToDisplay === void 0 ? void 0 : insuranceToDisplay.insured);
      const benefitsList = getBenefitsList(insuranceType, isSecondHand, showProofOfDelivery);
      const isThereBenefitsList = benefitsList.length > 0;
      const insuranceTitle = t(getInsuranceTitle(insuranceType, numberShipmentsInBulk), {
        number: numberShipmentsInBulk || formattedAmount
      });
      const isMaxCoverage = isChecked && !!maxCoverage && !!contentValue && contentValue > maxCoverage;
      const isPacklinkInsurance = [UpsellInsuranceType.PACKLINK_INSURANCE, UpsellInsuranceType.ENHANCED].includes(insuranceType);
      return jsxs(Fragment, {
        children: [isMaxCoverage && displayMaxCoverageInNewRow && jsx(GridChild, {
          colSpan: 12,
          css: getInsuranceMaxCoverageRow,
          children: jsx(MaxCoverageInlineNotification, {
            formattedMaxCoverage: formattedMaxCoverage
          })
        }), jsxs(GridChild, {
          colSpan: 12,
          colStart: showInsuranceInformation ? 1 : undefined,
          colSpanTablet: !hasVerticalLayout && showInsuranceInformation || useHalfWidth ? 6 : 12,
          children: [isMaxCoverage && !displayMaxCoverageInNewRow && jsx(MaxCoverageInlineNotification, {
            formattedMaxCoverage: formattedMaxCoverage,
            css: getInsuranceMaxCoverage
          }), jsx(InsuranceCard, {
            title: insuranceTitle,
            radioName: name,
            insuranceType: insuranceType,
            isChecked: isChecked,
            onChange: onChange,
            price: insuranceToDisplay === null || insuranceToDisplay === void 0 ? void 0 : insuranceToDisplay.basePrice,
            currency: currency,
            hasRadio: UpsellInsuranceType.STANDARD !== insuranceType,
            isRecommended: isPacklinkInsurance,
            children: isThereBenefitsList && jsx(InsuranceCardInfo, {
              benefitsList: benefitsList,
              amount: formattedAmount,
              showLink: isPacklinkInsurance
            })
          })]
        })]
      }, insuranceType);
    }), showInsuranceInformation && jsx(GridChild, {
      colSpan: 12,
      ...infoPosition,
      colSpanTablet: hasVerticalLayout && !useHalfWidth ? 12 : 6,
      children: jsx(InsuranceInformation, {
        imageUrl: imageUrl
      })
    })]
  });
};
const getInsuranceTitle = (insuranceType, numberShipmentsInBulk) => {
  if (numberShipmentsInBulk && insuranceType !== UpsellInsuranceType.NO_INSURANCE) {
    return InsuranceTitle.BULK;
  } else {
    return InsuranceTitle[insuranceType];
  }
};
const getBenefitsList = (insuranceType, isSecondHand, hasProofOfDelivery) => {
  const benefitsList = [];
  if (insuranceType === UpsellInsuranceType.PACKLINK_INSURANCE) {
    benefitsList.push(InsuranceBenefit.REFUND_LOSS, InsuranceBenefit.REFUND_DAMAGE);
    if (isSecondHand) {
      benefitsList.push(InsuranceBenefit.SECOND_HAND);
    }
  } else if (insuranceType === UpsellInsuranceType.STANDARD) {
    benefitsList.push(InsuranceBenefit.STANDARD_COMPENSATION);
  } else if ([UpsellInsuranceType.ENHANCED, UpsellInsuranceType.PROOF_OF_DELIVERY].includes(insuranceType)) {
    benefitsList.push(InsuranceBenefit.COMPENSATION);
    if (hasProofOfDelivery) {
      benefitsList.push(InsuranceBenefit.SIGNATURE);
    }
  }
  return benefitsList;
};
const MaxCoverageInlineNotification = _ref => {
  let {
    formattedMaxCoverage,
    ...rest
  } = _ref;
  return jsx(InlineNotification, {
    type: NotificationType.ALERT,
    ...rest,
    children: jsx(Trans, {
      i18nKey: "insurance-selector.max-coverage.text",
      values: {
        coverage: formattedMaxCoverage
      }
    })
  });
};

export { InsuranceSelector };
//# sourceMappingURL=InsuranceSelector.js.map
